import React, { useRef } from 'react';
import { Box, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  PieceToRebutField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
  PieceTypeSelector,
} from './shared';

// Peças que podem ser respondidas em uma manifestação
const PECAS_RESPONDER = [
  'Decisão Judicial',
  'Despacho',
  'Intimação',
  'Citação',
  'Sentença',
  'Acórdão',
  'Manifestação da Parte Contrária',
  'Parecer do Ministério Público',
  'Laudo Pericial',
  'Outro'
];

const ManifestacaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Manifestação Processual"
        customDescription="Documento para se manifestar em processo judicial"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Tipo da Peça (Breve ou Longa) */}
        <PieceTypeSelector
          value={values.tipo_peca || 'breve'}
          onChange={(value) => handleFieldChange('tipo_peca', value)}
          options={[
            { 
              value: 'breve', 
              label: 'Breve',
              description: 'Recomenda-se para gerar quesitos periciais, peças que sejam curtas e não exijam teses jurídicas.'
            },
            { 
              value: 'longa', 
              label: 'Longa',
              description: 'Recomenda-se para gerar peças que exijam maior desenvolvimento argumentativo e teses jurídicas.'
            },
          ]}
        />

        {/* 2. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
        />

        {/* 3. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Decisões judiciais, Despachos e Processo na íntegra."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 4. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva o contexto da manifestação, pontos importantes a serem abordados, argumentos a desenvolver..."
        />

        {/* 5. Peça que deseja responder */}
        <PieceToRebutField
          value={values.peca_rebater || ''}
          onChange={(value) => handleFieldChange('peca_rebater', value)}
          otherValue={values.peca_rebater_outro || ''}
          onOtherChange={(value) => handleFieldChange('peca_rebater_outro', value)}
          options={PECAS_RESPONDER}
          label="Indique a peça do processo que deseja rebater ou responder:"
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo o que deve ser manifestado que a IA irá transcrever automaticamente."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Anexe a decisão ou despacho que motivou a manifestação para que a IA possa gerar uma resposta adequada. O tipo de peça (Breve ou Longa) influencia diretamente no tamanho e profundidade da manifestação gerada." />
    </Box>
  );
};

export default ManifestacaoForm;

